import React, { useState } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. Our team will respond shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="bg-[#0B3D6E] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="font-label text-xs text-[#2E7BC7]">Contact</div>
          <h1 className="mt-3 font-display font-black text-4xl md:text-6xl">Let's talk freight.</h1>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-5 gap-10">
          <div className="md:col-span-2 space-y-6">
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-[#2E7BC7] mt-1" />
              <div><div className="font-display font-bold text-[#0B3D6E]">Sales & operations</div><div className="text-sm text-slate-600">778-861-7890</div></div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-[#2E7BC7] mt-1" />
              <div><div className="font-display font-bold text-[#0B3D6E]">Email</div><div className="text-sm text-slate-600">supplychain@apexscs.ca</div></div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-[#2E7BC7] mt-1" />
              <div><div className="font-display font-bold text-[#0B3D6E]">Headquarters</div><div className="text-sm text-slate-600">Vancouver, British Columbia<br />Canada</div></div>
            </div>
            <div className="mt-6 rounded-lg overflow-hidden border border-gray-200">
              <iframe
                title="Apex HQ Map — Vancouver, BC"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-123.15%2C49.26%2C-123.09%2C49.30&layer=mapnik&marker=49.2827%2C-123.1207"
                className="w-full h-56"
                loading="lazy"
              />
            </div>
          </div>
          <form onSubmit={submit} className="md:col-span-3 bg-gray-50 border border-gray-200 rounded-lg p-8 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name" v={form.name} on={upd("name")} required test="contact-name" />
              <Field label="Email" v={form.email} on={upd("email")} type="email" required test="contact-email" />
            </div>
            <Field label="Phone" v={form.phone} on={upd("phone")} test="contact-phone" />
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Message</label>
              <textarea rows={5} required value={form.message} onChange={upd("message")}
                className="w-full bg-white border border-gray-300 rounded-md px-4 py-2 focus:ring-2 focus:ring-[#2E7BC7] focus:border-transparent outline-none"
                data-testid="contact-message" />
            </div>
            <button disabled={loading} className="btn-primary-apex" data-testid="contact-submit-btn">
              {loading ? "Sending..." : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, v, on, type = "text", required, test }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700 mb-1 block">{label}</label>
      <input type={type} required={required} value={v} onChange={on}
        className="w-full bg-white border border-gray-300 rounded-md px-4 py-2 focus:ring-2 focus:ring-[#2E7BC7] focus:border-transparent outline-none"
        data-testid={test} />
    </div>
  );
}
